/**
 * SALA ACTUAL — a qué sala entró esta persona, y quién manda cuando hay dudas.
 *
 * El problema que resuelve (caso real): alguien entra con el código de una
 * sala y el Bolsillo lo lleva a OTRO proceso, porque la superficie elegía el
 * proceso por membresía («la primera participación activa») y no por la puerta
 * que la persona acababa de usar. Con una sola membresía nadie lo nota; con
 * dos, se aporta evidencia en la sala equivocada y no se entera nadie.
 *
 * Regla: EL CÓDIGO MANDA. El backend devuelve, junto al login por sala, cuál
 * sala abrió ese código (`TokenOut.sala`); acá la guardamos y `useSesionActiva`
 * la respeta por encima de cualquier heurística.
 *
 * Vive en localStorage a propósito: no es una credencial (el access token
 * sigue solo en memoria, C5), es un puntero — tiene que sobrevivir la recarga
 * de la página y el retorno del OAuth de Google.
 */
import { apiFetch } from './api';

export interface SalaEntrada {
  proceso_id: string;
  /** Puede venir vacío (retorno de Google): el nombre se resuelve después. */
  proceso_nombre: string;
  org_nombre: string;
  sesion_id: string | null;
  /** ISO YYYY-MM-DD, o null si la sala no tiene sesión materializada. */
  sesion_fecha: string | null;
  /** El proceso tiene PIN compartido: se entra escribiendo el nombre. */
  autoregistro: boolean;
}

const CLAVE = 'agora.sala';

/** La sala por la que se entró, o null si se llegó por otra vía. */
export function leerSala(): SalaEntrada | null {
  try {
    const crudo = window.localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const dato = JSON.parse(crudo) as Partial<SalaEntrada>;
    if (!dato || typeof dato.proceso_id !== 'string' || !dato.proceso_id) return null;
    return {
      proceso_id: dato.proceso_id,
      proceso_nombre: dato.proceso_nombre ?? '',
      org_nombre: dato.org_nombre ?? '',
      sesion_id: dato.sesion_id ?? null,
      sesion_fecha: dato.sesion_fecha ?? null,
      autoregistro: Boolean(dato.autoregistro),
    };
  } catch {
    return null; // localStorage bloqueado o JSON corrupto: no es fatal
  }
}

/** Fija la sala del código (o la limpia al cerrar sesión). */
export function guardarSala(sala: SalaEntrada | null): void {
  try {
    if (sala === null) window.localStorage.removeItem(CLAVE);
    else window.localStorage.setItem(CLAVE, JSON.stringify(sala));
  } catch {
    /* modo privado sin storage: se pierde al recargar, no rompe nada */
  }
}

/**
 * Qué sala abre este código, ANTES de pedir el PIN. Público: no devuelve el
 * PIN ni nada que la Pantalla del salón no proyecte ya. Lanza ApiError (404 si
 * el código no existe, 409 si la sala ya cerró o el código venció).
 */
export function previsualizarSala(codigo: string): Promise<SalaEntrada> {
  return apiFetch<SalaEntrada>(`/auth/sala?codigo=${encodeURIComponent(codigo.trim())}`, {
    skipRefresh: true,
  });
}

/**
 * Sumarse a una sala YA autenticado (Google sin QR, o el QR de otra jornada).
 * No pide PIN: es la misma regla del camino QR → Google → adentro. Devuelve la
 * sala y la deja fijada como puntero, así el Bolsillo abre ESA y no otra.
 *
 * Lanza ApiError (404 código inexistente, 409 sala cerrada/vencida).
 */
export async function unirmeASala(codigo: string): Promise<SalaEntrada> {
  const sala = await apiFetch<SalaEntrada>('/auth/sala/unirme', {
    method: 'POST',
    json: { codigo: codigo.trim() },
  });
  guardarSala(sala);
  return sala;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** '2026-08-03' -> 'sesión del 3 de agosto'. Cadena vacía si no hay fecha. */
export function fechaSalaHumana(iso: string | null): string {
  if (!iso) return '';
  const [a, m, d] = iso.split('-').map(Number);
  if (!a || !m || !d) return '';
  return `sesión del ${d} de ${MESES[m - 1]}`;
}
